import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, GripVertical } from 'lucide-react';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

interface FAQEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const FAQEditor: React.FC<FAQEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const faqs = content?.faqs || [];
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleAddFAQ = () => {
    onContentChange({
      faqs: [
        ...faqs,
        {
          question: '',
          answer: '',
          category: 'Genel'
        }
      ]
    });
  };
  
  const handleRemoveFAQ = (index: number) => {
    const newFaqs = [...faqs];
    newFaqs.splice(index, 1);
    onContentChange({ faqs: newFaqs });
  };
  
  const handleFAQChange = (index: number, field: keyof FAQ, value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    onContentChange({ faqs: newFaqs });
    
    // Clear error for this field if it exists
    if (errors[`faq-${index}-${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`faq-${index}-${field}`];
        return newErrors;
      });
    }
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || ''}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Alt Başlık
            </label>
            <input
              type="text"
              name="subtitle"
              value={settings?.subtitle || ''}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Maksimum Soru Sayısı
            </label>
            <input
              type="number"
              name="maxItems"
              value={settings?.maxItems || 10}
              onChange={handleSettingChange}
              min={1}
              max={20}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="showCategories"
                checked={settings?.showCategories || false}
                onChange={handleSettingChange}
                className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
              />
              <span className="text-sm text-slate-600">Kategorileri Göster</span>
            </label>
          </div>
        </div>
      </div>
      
      {/* FAQ Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-slate-700">Soru ve Cevaplar</h3>
          <button
            onClick={handleAddFAQ}
            disabled={faqs.length >= (settings?.maxItems || 10)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={14} />
            <span>Soru Ekle</span>
          </button>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq: FAQ, index: number) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical size={16} className="text-slate-400 cursor-move" />
                  <span className="text-sm font-medium text-slate-700">Soru {index + 1}</span>
                </div>
                <button
                  onClick={() => handleRemoveFAQ(index)}
                  className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs text-slate-500">
                  Soru
                </label>
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors[`faq-${index}-question`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="Soru metni"
                />
                {errors[`faq-${index}-question`] && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {errors[`faq-${index}-question`]}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs text-slate-500">
                  Cevap
                </label>
                <textarea
                  value={faq.answer}
                  onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors[`faq-${index}-answer`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="Cevap metni"
                />
                {errors[`faq-${index}-answer`] && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {errors[`faq-${index}-answer`]}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs text-slate-500">
                  Kategori
                </label>
                <select
                  value={faq.category}
                  onChange={(e) => handleFAQChange(index, 'category', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                >
                  <option value="Genel">Genel</option>
                  <option value="Kredi Başvurusu">Kredi Başvurusu</option>
                  <option value="Kredi Hesaplama">Kredi Hesaplama</option>
                  <option value="Kredi Notu">Kredi Notu</option>
                  <option value="Ödemeler ve Vadeler">Ödemeler ve Vadeler</option>
                  <option value="Özel Durumlar">Özel Durumlar</option>
                </select>
              </div>
            </div>
          ))}
          
          {faqs.length === 0 && (
            <div className="text-center p-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <p className="text-slate-500 mb-2">Henüz soru eklenmedi</p>
              <button
                onClick={handleAddFAQ}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
              >
                <Plus size={16} />
                <span>İlk Soruyu Ekle</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQEditor;